/**
 * The membership plan catalog (ELARA-1012).
 *
 * WHY THIS EXISTS: adding a plan used to mean editing nine files — the plan enum, the
 * requires-payment check, a new price-ID env var, price selection in the Stripe service, the
 * provisioning branch, three `'annual'` fallbacks, and the hardcoded copy on the payment screen —
 * plus a workflow edit and a production deploy. Pricing experiments cost a release each.
 *
 * The reason it was that expensive is that Elara duplicated what Stripe already knows. A Stripe
 * Price carries the amount, the currency and the interval; re-encoding those as a
 * closed enum plus hardcoded strings is what made every experiment a code change. So this table
 * holds ONLY what Stripe cannot: the plan's identity, its display copy, and the two policy flags
 * below. Everything monetary is read from the price at runtime and never stored here.
 *
 * ADDING A PLAN: add an id to {@link MEMBERSHIP_PLAN_IDS}, add its entry here, and add its price ID
 * to the `STRIPE_PLAN_PRICE_IDS` GitHub Variable (a JSON map, so no workflow file changes and no new
 * `_PREVIEW` variable per plan). Nothing else in the codebase needs touching — the payment screen,
 * the gate, provisioning and the projection all read this table.
 */

import { z } from 'zod';

/**
 * Every plan id, in one tuple, so the Zod enum and the catalog below cannot drift apart.
 *
 * These strings are STORED on profile and invite-code documents. Renaming one rewrites member data,
 * so treat them as a persisted contract rather than display text — the display name is a field.
 */
export const MEMBERSHIP_PLAN_IDS = ['annual', 'free_lifetime'] as const;
export type MembershipPlanId = (typeof MEMBERSHIP_PLAN_IDS)[number];

export interface MembershipPlanDefinition {
  /** Shown on the payment screen. Safe to change — nothing keys off it. */
  name: string;
  /**
   * Whether a first payment is owed at signup.
   *
   * The ONLY behavioural fork in the whole membership flow. `free_lifetime` is not a special case
   * in code any more — it is simply a plan with this set to false, which is what keeps comped
   * members away from the payment screen.
   */
  requiresPayment: boolean;
  /**
   * Whether a member may choose this plan for themselves at signup.
   *
   * Load-bearing for the trust boundary #520 established: the invite code decides WHETHER payment
   * is owed, and only plans marked selectable may be picked from the payment screen. Without this a
   * member could self-select a comped plan by sending its id, so `StartMembershipPayment` refuses
   * any plan that is not both selectable and paid.
   */
  selectable: boolean;
  // No trial length here on purpose. A free trial is an offer made to a person, so it lives on the
  // invite code (`InviteCodeSpec.trialDays`) and is copied onto their profile at signup. Keeping a
  // second copy on the plan meant two sources for one number and a rule for which one won.
  /** Bullets on the plan card. Per-plan so an experiment can pitch a plan differently. */
  benefits: readonly MembershipPlanBenefit[];
}

export interface MembershipPlanBenefit {
  /**
   * Icon name for the bullet. An opaque string here on purpose: the web shell maps it through
   * MaterialIcon and a native shell may map it elsewhere or ignore it, so the catalog stays free of
   * either platform's icon set.
   */
  icon: string;
  text: string;
  /**
   * Shown only to a member joining for the first time. For an offer that a returning member has
   * already used, such as the first-trip guarantee.
   */
  firstTimeOnly?: boolean;
}

/**
 * Benefits shared by the paid plans. Extracted so a new paid plan inherits the pitch by default and
 * an experiment can override it deliberately rather than by omission.
 */
const PAID_MEMBERSHIP_BENEFITS: readonly MembershipPlanBenefit[] = [
  { icon: 'support_agent', text: 'Dedicated human travel advisor available by phone, text, and email' },
  { icon: 'verified', text: 'Exclusive perks & upgrades at 1,200 member hotels' },
  { icon: 'hotel', text: 'A curated collection of 6,000+ hotels worldwide' },
  { icon: 'stars_2', text: 'Smarter travel recommendations' },
  { icon: 'favorite', text: 'Love your first trip or we’ll return your membership fee', firstTimeOnly: true },
];

export const MEMBERSHIP_PLANS: Record<MembershipPlanId, MembershipPlanDefinition> = {
  annual: {
    name: 'Annual membership',
    requiresPayment: true,
    selectable: true,
    benefits: PAID_MEMBERSHIP_BENEFITS,
  },
  free_lifetime: {
    name: 'Lifetime membership',
    requiresPayment: false,
    // Never offered as a choice — it is granted by a comped invite code and nothing else.
    selectable: false,
    benefits: PAID_MEMBERSHIP_BENEFITS,
  },
};

/** The plan ids as a Zod enum. Built from the tuple above so the two cannot disagree. */
export const MembershipPlanIdEnum = z.enum(MEMBERSHIP_PLAN_IDS);

/**
 * Look up a plan, or `undefined` for an id this build does not know.
 *
 * An unknown id is reachable in production: a member's profile can name a plan that a later release
 * removed from the table. Callers must decide what that means rather than assuming — see
 * `requiresInitialPayment`, which treats it as payment-owed so such a member still has an exit.
 */
export function getMembershipPlan(id: string | undefined): MembershipPlanDefinition | undefined {
  if (!id) return;
  return Object.hasOwn(MEMBERSHIP_PLANS, id) ? MEMBERSHIP_PLANS[id as MembershipPlanId] : undefined;
}

/** True when this id names a plan this build knows. Narrows a stored string to a known id. */
export function isMembershipPlanId(id: string | undefined): id is MembershipPlanId {
  return getMembershipPlan(id) !== undefined;
}

/**
 * The plans a member may choose from on the payment screen, in catalog order.
 *
 * Both flags are required: `selectable` is the policy, and `requiresPayment` keeps a hypothetical
 * free-but-selectable entry out of a screen whose whole job is collecting a card.
 */
export function selectableMembershipPlans(): Array<MembershipPlanDefinition & { id: MembershipPlanId }> {
  return MEMBERSHIP_PLAN_IDS.filter((id) => MEMBERSHIP_PLANS[id].selectable && MEMBERSHIP_PLANS[id].requiresPayment).map(
    (id) => ({ id, ...MEMBERSHIP_PLANS[id] }),
  );
}

/**
 * Every plan an operator may mint onto an invite code, in catalog order.
 *
 * Deliberately unfiltered, and deliberately NOT `selectableMembershipPlans`. Those two answer
 * different questions: `selectable` is what a *member* may choose for themselves at signup, which
 * excludes `free_lifetime` precisely so nobody can self-grant a comp. An *operator* minting a code
 * is the trusted side of that boundary — granting the comp is the whole point of the field — so the
 * admin picker offers the full catalog and a new plan appears in it the moment it is added here.
 */
export function mintableMembershipPlans(): Array<MembershipPlanDefinition & { id: MembershipPlanId }> {
  return MEMBERSHIP_PLAN_IDS.map((id) => ({ id, ...MEMBERSHIP_PLANS[id] }));
}

/**
 * Whether a first payment is owed on this plan, treating an id this build does not know as paid.
 *
 * The conservative default is the point. An unknown id reaches here from a stored document written
 * by a later release; reading it as comped would hand out a free membership on a typo, while
 * reading it as paid leaves the member an exit through the card form.
 */
export function planRequiresPayment(id: string | undefined): boolean {
  return getMembershipPlan(id)?.requiresPayment ?? true;
}

/** Whether this plan grants membership at no charge — the inverse of {@link planRequiresPayment}. */
export function isCompedPlan(id: string | undefined): boolean {
  return !planRequiresPayment(id);
}
